import React, { useRef, useState } from "react";
import Button from "../../../components/ui/Button";

const QRCodeViewerModal = ({ isOpen, onClose, qrCode, customer }) => {
  const cardRef = useRef(null);
  const [downloading, setDownloading] = useState(false);

  if (!isOpen || !qrCode) return null;

  const customerName = customer?.name || "N/A";
  const cin = customer?.cin || "N/A";
  const usn = customer?.usn || "N/A";
  const village = customer?.village || "N/A";
  const companyPhone = "9492272320";

  const handleDownload = async () => {
    if (!cardRef.current) return;

    try {
      setDownloading(true);

      // Dynamically import html2canvas
      const html2canvas = (await import("html2canvas")).default;

      // Capture the card element
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: "#ffffff",
        scale: 2, // Higher quality
        logging: false,
        useCORS: true,
      });

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error("Failed to create image blob");
        }

        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `QR_Card_${serviceNumber || "customer"}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, "image/png");
    } catch (error) {
      console.error("Error downloading QR card:", error);
      alert("Failed to download QR card. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-card border border-border rounded-lg shadow-lg w-full max-w-4xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">
              QR Code Card
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              View and download customer QR code card
            </p>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} />
        </div>

        {/* Card Preview */}
        <div className="p-6 flex flex-col items-center overflow-auto">
          <div
            ref={cardRef}
            className="bg-white rounded-lg shadow-lg overflow-hidden"
            style={{ width: "800px", minHeight: "400px" }}
          >
            <div className="flex h-full">
              {/* Left Half - QR Code */}
              <div className="w-1/2 bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center p-8 border-r-2 border-gray-200">
                <div className="bg-white p-6 rounded-lg shadow-md">
                  <img
                    src={qrCode}
                    alt={`QR Code for ${cin}`}
                    className="w-64 h-64 object-contain"
                  />
                </div>
              </div>

              {/* Right Half - Customer Details */}
              <div className="w-1/2 p-8 flex flex-col justify-center bg-white">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-1">
                      {customerName}
                    </h3>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                      Customer Name
                    </p>
                  </div>

                  <div className="border-t border-gray-200 pt-4 space-y-4">
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        CIN
                      </p>
                      <p className="text-lg font-mono text-gray-900">{cin}</p>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        Service Number (USN)
                      </p>
                      <p className="text-base font-mono text-gray-900">{usn}</p>
                    </div>

                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-1">
                        Village
                      </p>
                      <p className="text-base text-gray-900">{village}</p>
                    </div>

                    <div className="pt-2 border-t border-gray-200 text-xs text-gray-500">
                      Valence Energies • {companyPhone}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={downloading}>
            Close
          </Button>
          <Button
            variant="default"
            iconName="Download"
            iconPosition="left"
            onClick={handleDownload}
            loading={downloading}
            disabled={downloading}
          >
            Download Card
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QRCodeViewerModal;
